import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { api, type User, type Workspace } from '../api/client';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  workspaces: (Workspace & { role?: string })[];
  currentWorkspaceId: string | undefined;
  login: (provider: 'google' | 'github') => void;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  switchWorkspace: (wsId: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<(Workspace & { role?: string })[]>([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getMe();
      setUser(data.user);
      // Prefer session workspace (set by workspace switching), fall back to default
      setCurrentWorkspaceId(data.session?.workspace_id || data.user.default_workspace_id);
      // Fetch workspaces
      try {
        const wsData = await api.getWorkspaces();
        setWorkspaces(wsData.workspaces);
      } catch {
        setWorkspaces([]);
      }
    } catch {
      setUser(null);
      setWorkspaces([]);
      setCurrentWorkspaceId(undefined);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback((provider: 'google' | 'github') => {
    window.location.href = `/auth/${provider}/authorize`;
  }, []);

  const loginWithEmail = useCallback(async (email: string, password: string) => {
    setError(null);
    await api.login(email, password);
    // Refresh full state from /auth/me (includes workspaces, session, onboarding)
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Ignore logout errors
    } finally {
      setUser(null);
      setWorkspaces([]);
      setCurrentWorkspaceId(undefined);
    }
  }, []);

  const switchWorkspace = useCallback(async (wsId: string) => {
    await api.switchWorkspace(wsId);
    setCurrentWorkspaceId(wsId);
  }, []);

  const value: AuthState = {
    user,
    loading,
    error,
    isAuthenticated: user !== null,
    workspaces,
    currentWorkspaceId,
    login,
    loginWithEmail,
    logout,
    refresh,
    switchWorkspace,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
